import React, {useMemo, useState} from 'react';
import {getApp, getApps, initializeApp} from 'firebase/app';
import {getAuth, GoogleAuthProvider, inMemoryPersistence, setPersistence, signInWithPopup, signOut} from 'firebase/auth';
import '../components/team-invite/TeamInviteView.css';

export function parseDesktopAuthRequest(hash) {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const port = params.get('port');
  const state = params.get('state');
  if (!/^\d{1,5}$/.test(port || '') || Number(port) < 1024 || Number(port) > 65535 || !/^[a-f0-9]{64}$/.test(state || '')) return null;
  return {port, state};
}

export default function DesktopGoogleSignIn() {
  const request = useMemo(() => parseDesktopAuthRequest(window.location.hash), []);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');

  async function send(payload) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`http://127.0.0.1:${request.port}/google-sign-in`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({...payload, state:request.state}), signal:controller.signal,
        credentials:'omit', cache:'no-store', referrerPolicy:'no-referrer',
      });
      if (!response.ok) throw new Error('The desktop sign-in attempt has expired. Start again in Valovate.');
    } finally {clearTimeout(timeout);}
  }

  async function signIn() {
    if (!request || busy) return;
    setBusy(true); setError('');
    let browserAuth;
    try {
      // Separate, memory-only auth keeps the user's existing website session intact.
      const app = getApps().find(app => app.name === 'desktop-google-sign-in') || initializeApp(getApp().options, 'desktop-google-sign-in');
      browserAuth = getAuth(app);
      await setPersistence(browserAuth, inMemoryPersistence);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({prompt:'select_account'});
      const result = await signInWithPopup(browserAuth, provider);
      const idToken = GoogleAuthProvider.credentialFromResult(result)?.idToken;
      if (!idToken) throw new Error('Google did not return a sign-in credential. Please try again.');
      await send({idToken});
      setComplete(true);
      window.history.replaceState(null, '', window.location.pathname);
    } catch (failure) {
      setError(failure.code === 'auth/popup-closed-by-user' ? 'Google sign-in was cancelled. You can try again.' :
        failure.code === 'auth/popup-blocked' ? 'Allow the Google sign-in popup, then try again.' :
        failure instanceof TypeError || failure.name === 'AbortError' ? 'Could not reach Valovate. Keep the desktop app open and allow this page to connect to your device if your browser asks.' :
        failure.message || 'Google sign-in failed. Please try again.');
    } finally {
      if (browserAuth) await signOut(browserAuth).catch(() => {});
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {await send({cancel:true});} catch (_) { /* The app may already have closed this attempt. */ }
    setCancelled(true); setBusy(false);
    window.history.replaceState(null, '', window.location.pathname);
  }

  return <main className="TeamInvite">
    <div className="TeamInvite__brand"><span>V</span> VALOVATE</div>
    <article className="TeamInvite__card">
      <div className="TeamInvite__intro">
        <p className="TeamInvite__eyebrow">Desktop sign-in</p>
        <h1>{complete ? 'Return to Valovate' : cancelled ? 'Sign-in cancelled' : 'Continue with Google'}</h1>
        <p className="TeamInvite__copy">Choose your Google account here to sign into the Valovate desktop app.</p>
      </div>
      <div className="TeamInvite__actions">
        {!request ? <p role="alert">Start Google sign-in from the Valovate desktop app to open a valid sign-in request.</p> :
          complete ? <p role="status">Your sign-in was sent to Valovate. Return to the desktop app to continue. You can close this tab.</p> :
          cancelled ? <p role="status">You can close this tab and start again in Valovate.</p> : <>
            <p>Only continue if you just requested Google sign-in in Valovate on this PC.</p>
            <button className="TeamInvite__primary" onClick={signIn} disabled={busy}>{busy ? 'Waiting for sign-in…' : 'Continue with Google'}</button>
            <button className="TeamInvite__secondary app-cancel-control" onClick={cancel} disabled={busy}>Cancel sign-in</button>
          </>}
        {error && <p className="TeamInvite__error" role="alert">{error}</p>}
      </div>
    </article>
  </main>;
}
